import React, { useState, useEffect } from 'react';

const NewPostModal = ({ isOpen, onClose, onSave, editingPost = null }) => {
  const [formData, setFormData] = useState({
    caption: '',
    platforms: [],
    scheduledDate: '',
    scheduledTime: '',
    media: null,
    status: 'draft'
  });

  useEffect(() => {
    if (editingPost) {
      setFormData({
        caption: editingPost.title || '',
        platforms: [editingPost.platform] || [],
        scheduledDate: editingPost.scheduledDate ? 
          new Date(editingPost.scheduledDate).toISOString().split('T')[0] : '',
        scheduledTime: editingPost.time || '',
        media: null,
        status: editingPost.status || 'draft'
      });
    } else {
      resetForm();
    }
  }, [editingPost, isOpen]);

  const resetForm = () => {
    setFormData({
      caption: '',
      platforms: [],
      scheduledDate: '',
      scheduledTime: '',
      media: null,
      status: 'draft'
    });
  };

  const handlePlatformToggle = (platform) => {
    setFormData(prev => ({
      ...prev,
      platforms: prev.platforms.includes(platform)
        ? prev.platforms.filter(p => p !== platform)
        : [...prev.platforms, platform]
    }));
  };

  const handleSubmit = (status) => {
    const post = {
      id: editingPost?.id || Date.now(),
      title: formData.caption,
      platform: formData.platforms[0] || 'Facebook',
      platforms: formData.platforms,
      scheduledDate: new Date(`${formData.scheduledDate}T${formData.scheduledTime}`),
      time: formData.scheduledTime,
      status: status,
      media: formData.media
    };
    onSave(post);
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  const platforms = ['Facebook', 'Instagram', 'LinkedIn', 'Twitter'];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">
            {editingPost ? 'Edit Post' : 'Create New Post'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form Content */}
        <div className="p-6 space-y-6">
          {/* Caption */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Caption
            </label>
            <textarea
              value={formData.caption}
              onChange={(e) => setFormData({ ...formData, caption: e.target.value })}
              rows={4}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
              placeholder="Write your post caption..."
            />
          </div>

          {/* Platform Selection */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Select Platforms
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {platforms.map(platform => (
                <button
                  key={platform}
                  type="button"
                  onClick={() => handlePlatformToggle(platform)}
                  className={`p-3 rounded-lg border-2 transition-all ${
                    formData.platforms.includes(platform)
                      ? 'border-indigo-600 bg-indigo-50 text-indigo-700'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <div className="text-sm font-medium">{platform}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Media Upload */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Media (Optional)
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-indigo-400 transition-colors cursor-pointer">
              <input
                type="file"
                accept="image/*,video/*"
                onChange={(e) => setFormData({ ...formData, media: e.target.files[0] })}
                className="hidden"
                id="media-upload"
              />
              <label htmlFor="media-upload" className="cursor-pointer">
                <svg className="w-12 h-12 mx-auto text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <p className="text-sm text-gray-600">Click to upload or drag and drop</p>
                <p className="text-xs text-gray-500 mt-1">PNG, JPG, GIF, MP4 up to 10MB</p>
                {formData.media && (
                  <p className="text-sm text-indigo-600 font-medium mt-2">
                    {formData.media.name}
                  </p>
                )}
              </label>
            </div>
          </div>

          {/* Date & Time */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Date
              </label>
              <input
                type="date"
                value={formData.scheduledDate}
                onChange={(e) => setFormData({ ...formData, scheduledDate: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Time
              </label>
              <input
                type="time"
                value={formData.scheduledTime}
                onChange={(e) => setFormData({ ...formData, scheduledTime: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
          <button
            onClick={onClose}
            className="px-5 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => handleSubmit('draft')}
            className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors"
          >
            Save as Draft
          </button>
          <button
            onClick={() => handleSubmit('scheduled')}
            className="px-5 py-2.5 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors shadow-sm"
          >
            Schedule Post
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewPostModal;
